import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageNavbar from './PageNavbar';
import api from '../config/api';
import { PosDeviceContext } from '../context/PosDeviceContext';
import './HubShell.css';

const formatInr2 = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

export default function OwnerHub() {
  const { enrolled: posDeviceEnrolled } = useContext(PosDeviceContext);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get('/owner/overview', { params: { days } });
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.error || 'Could not load overview');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  return (
    <div className="cc-page">
      <PageNavbar />
      <header className="cc-page-head">
        <h1 className="cc-page-title">Owner overview</h1>
        <p className="cc-page-lead">
          Franchise pulse: scheduled OMS orders, counter POS, and CRM leads — all outlets in one glance. Tune the window
          for a rolling snapshot.
        </p>
      </header>

      <div className="cc-card" style={{ marginBottom: '1rem' }}>
        <label className="owner-range-label">
          <span>Reporting window</span>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="owner-range-select">
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        </label>
      </div>

      {err ? <div className="cc-alert error" style={{ marginBottom: 12 }}>{err}</div> : null}

      {!data && !err ? (
        <div className="cc-metric-grid">
          {[1, 2, 3, 4].map((k) => (
            <div key={k} className="cc-metric cc-metric--skeleton" />
          ))}
        </div>
      ) : null}

      {data ? (
        <>
          <p className="owner-range-caption">
            Range: <strong>{data.range.start}</strong> → <strong>{data.range.end}</strong> ({data.range.days} days)
          </p>

          <div className="cc-metric-grid">
            <div className="cc-metric">
              <div className="cc-metric-label">OMS orders</div>
              <div className="cc-metric-value">{data.orders.count}</div>
              <div className="cc-metric-sub">Line items ≈ {formatInr2(data.orders.revenue)}</div>
            </div>
            <div className="cc-metric">
              <div className="cc-metric-label">POS (walk-in)</div>
              <div className="cc-metric-value">{data.pos.count}</div>
              <div className="cc-metric-sub">{formatInr2(data.pos.revenue)}</div>
            </div>
            <div className="cc-metric">
              <div className="cc-metric-label">CRM leads</div>
              <div className="cc-metric-value">{data.crm.newLeadsInRange}</div>
              <div className="cc-metric-sub">New in window</div>
            </div>
          </div>

          {data.crm.byStatus && Object.keys(data.crm.byStatus).length ? (
            <div className="cc-card" style={{ marginBottom: '1rem' }}>
              <h2 className="cc-card-title" style={{ textTransform: 'none', fontSize: '0.85rem', color: 'var(--cc-muted)' }}>
                Lead status mix
              </h2>
              <div className="owner-status-chips">
                {Object.entries(data.crm.byStatus).map(([k, v]) => (
                  <span key={k} className="owner-chip">
                    {k}: <strong>{v}</strong>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <h3 className="cc-section-label">Outlets</h3>
          {data.stores?.length ? (
            <div className="cc-store-grid">
              {data.stores.map((s) => (
                <div key={s.id} className="cc-store-card">
                  <h4>{s.name}</h4>
                  <dl>
                    <div>
                      <dt>Code</dt>
                      <dd>
                        <code>{s.code}</code>
                      </dd>
                    </div>
                    <div>
                      <dt>City</dt>
                      <dd>{s.city || '—'}</dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>{s.is_active ? 'Active' : 'Inactive'}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : (
            <div className="cc-card">
              <p style={{ margin: 0, color: 'var(--cc-muted)', fontSize: '0.95rem' }}>
                No stores in database yet — add outlets from User management.
              </p>
            </div>
          )}

          <div className="owner-footer-actions">
            <Link to="/" className="btn btn-secondary">
              Dashboard
            </Link>
            {posDeviceEnrolled ? (
              <Link to="/pos" className="btn btn-primary">
                Open POS & CRM
              </Link>
            ) : null}
            <Link to="/orders" className="btn btn-secondary">
              Orders
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
}
